import { apiService } from './api';

export interface DateRange {
  from: string;
  to: string;
}

export interface OverviewStats {
  totalClients: {
    value: number;
    change: number;
    type: 'increase' | 'decrease';
  };
  activeClients: {
    value: number;
    change: number;
    type: 'increase' | 'decrease';
  };
  totalIngredients: {
    value: number;
    change: number;
    type: 'increase' | 'decrease';
  };
  totalDiets: {
    value: number;
    change: number;
    type: 'increase' | 'decrease';
  };
  totalRecipes: {
    value: number;
    change: number;
    type: 'increase' | 'decrease';
  };
  totalMenus: {
    value: number;
    change: number;
    type: 'increase' | 'decrease';
  };
}

export interface ClientAnalytics {
  statusDistribution: Array<{
    status: string;
    _count: { status: number };
  }>;
  growthByDate: Record<string, number>;
  demographics: {
    ageGroups: Array<{ name: string; value: number }>;
    genderDistribution: Array<{ name: string; value: number }>;
  };
  totalClients: number;
}

export interface IngredientAnalytics {
  mostUsed: Array<{
    name: string;
    usage: number;
    category: string;
  }>;
  categoryDistribution: Array<{
    category: string;
    count: number;
  }>;
}

export interface DietAnalytics {
  distribution: Array<{
    name: string;
    menus: number;
    description: string;
  }>;
  performance: Array<{
    name: string;
    menus: number;
    satisfaction: string;
    completion: number;
    effectiveness: string;
  }>;
}

export interface RevenueAnalytics {
  dailyRevenue: Array<{
    date: string;
    revenue: number;
    subscriptions: number;
    consultations: number;
    products: number;
  }>;
  totalRevenue: number;
  avgDailyRevenue: number;
  revenueBySource: Array<{
    source: string;
    amount: number;
  }>;
}

export interface MenuPreparationTotalsItem {
  plateId: string;
  plateName: string;
  size?: string;
  measureUnitAbbr?: string;
  count: number;
}

export interface MenuPreparationAssignmentItem {
  clientId: string;
  clientName: string;
  routeName?: string;
  dietName?: string;
  plateId: string;
  plateName: string;
  size?: string;
  measureUnitAbbr?: string;
}

export interface MenuPreparationIngredientTotalsItem {
  ingredientId: string;
  ingredientName: string;
  measureUnitAbbr?: string;
  totalQuantity: number;
}

export interface MenuPreparationAnalyticsResponse {
  dietId: string;
  dietName: string;
  serviceDate: string;
  mealType: string;
  menuId: string | null;
  menuName: string | null;
  weekIndex: number | null;
  dayOfWeek: number;
  dayLabel: string;
  clientCount: number;
  totals: MenuPreparationTotalsItem[];
  assignments: MenuPreparationAssignmentItem[];
  ingredientTotals: MenuPreparationIngredientTotalsItem[];
}

export interface DailyProductionRow {
  clientId: string;
  clientName: string;
  dietId: string;
  dietName: string;
  routeName?: string;
  mealType: string;
  plateId: string;
  plateName: string;
  ingredientsList: string;
  category?: string;
  allergies?: string | null;
  dietaryPreferences?: string | null;
}

export interface DailyProductionAnalyticsResponse {
  serviceDate: string;
  dayOfWeek: number;
  rows: DailyProductionRow[];
}

class AnalyticsService {
  async getOverviewStats(dateRange?: DateRange): Promise<OverviewStats> {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('from', dateRange.from);
    if (dateRange?.to) params.append('to', dateRange.to);

    return apiService.get(`/analytics/overview?${params.toString()}`);
  }

  async getClientAnalytics(dateRange?: DateRange): Promise<ClientAnalytics> {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('from', dateRange.from);
    if (dateRange?.to) params.append('to', dateRange.to);

    return apiService.get(`/analytics/clients?${params.toString()}`);
  }

  async getIngredientAnalytics(dateRange?: DateRange): Promise<IngredientAnalytics> {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('from', dateRange.from);
    if (dateRange?.to) params.append('to', dateRange.to);

    return apiService.get(`/analytics/ingredients?${params.toString()}`);
  }

  async getDietAnalytics(dateRange?: DateRange): Promise<DietAnalytics> {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('from', dateRange.from);
    if (dateRange?.to) params.append('to', dateRange.to);

    return apiService.get(`/analytics/diets?${params.toString()}`);
  }

  async getRevenueAnalytics(dateRange?: DateRange): Promise<RevenueAnalytics> {
    const params = new URLSearchParams();
    if (dateRange?.from) params.append('from', dateRange.from);
    if (dateRange?.to) params.append('to', dateRange.to);

    return apiService.get(`/analytics/revenue?${params.toString()}`);
  }

  async getMenuPreparationAnalytics(paramsInput: {
    dietId: string;
    serviceDate: string;
    mealType: string;
    routeDeliveryId?: string;
    locationId?: string;
    status?: string;
  }): Promise<MenuPreparationAnalyticsResponse> {
    const params = new URLSearchParams();
    params.append('dietId', paramsInput.dietId);
    params.append('serviceDate', paramsInput.serviceDate);
    params.append('mealType', paramsInput.mealType);
    if (paramsInput.routeDeliveryId) params.append('routeDeliveryId', paramsInput.routeDeliveryId);
    if (paramsInput.locationId) params.append('locationId', paramsInput.locationId);
    if (paramsInput.status) params.append('status', paramsInput.status);

    return apiService.get(`/analytics/menu-preparation?${params.toString()}`);
  }

  async getChefCountAnalytics(paramsInput: {
    serviceDate: string;
    mealType?: string;
    routeDeliveryId?: string;
    locationId?: string;
    status?: string;
  }): Promise<{
    success: boolean;
    serviceDate: string;
    mealType: string;
    clientCount: number;
    ingredientCounts: Array<{
      ingredientName: string;
      count: number;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append('serviceDate', paramsInput.serviceDate);
    if (paramsInput.mealType) params.append('mealType', paramsInput.mealType);
    if (paramsInput.routeDeliveryId) params.append('routeDeliveryId', paramsInput.routeDeliveryId);
    if (paramsInput.locationId) params.append('locationId', paramsInput.locationId);
    if (paramsInput.status) params.append('status', paramsInput.status);

    return apiService.get(`/analytics/chef-count?${params.toString()}`);
  }

  async exportMenuPreparationReport(paramsInput: {
    dietId: string;
    serviceDate: string;
    mealType: string;
    routeDeliveryId?: string;
    locationId?: string;
    status?: string;
  }): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('type', 'menu-preparation');
    params.append('dietId', paramsInput.dietId);
    params.append('serviceDate', paramsInput.serviceDate);
    params.append('mealType', paramsInput.mealType);
    if (paramsInput.routeDeliveryId) params.append('routeDeliveryId', paramsInput.routeDeliveryId);
    if (paramsInput.locationId) params.append('locationId', paramsInput.locationId);
    if (paramsInput.status) params.append('status', paramsInput.status);

    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/analytics/export?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // Export report functionality
  async exportReport(type: string, dateRange?: DateRange): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('type', type);
    if (dateRange?.from) params.append('from', dateRange.from);
    if (dateRange?.to) params.append('to', dateRange.to);

    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/analytics/export?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // Helper method to download exported report
  downloadReport(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async getDailyProductionAnalytics(paramsInput: {
    serviceDate: string;
    routeDeliveryId?: string;
    locationId?: string;
    status?: string;
  }): Promise<DailyProductionAnalyticsResponse> {
    const params = new URLSearchParams();
    params.append('serviceDate', paramsInput.serviceDate);
    if (paramsInput.routeDeliveryId) params.append('routeDeliveryId', paramsInput.routeDeliveryId);
    if (paramsInput.locationId) params.append('locationId', paramsInput.locationId);
    if (paramsInput.status) params.append('status', paramsInput.status);

    return apiService.get(`/analytics/daily-production?${params.toString()}`);
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
